import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from '../components/Helmet';

const content = `
# Mentions légales

Dernière mise à jour : 29 juin 2026

En vertu de l'article 6 de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l'économie numérique, il est précisé aux utilisateurs du site https://www.upnov.com l'identité des différents intervenants dans le cadre de sa réalisation et de son suivi.

## 1. Éditeur du site

Le site https://www.upnov.com est édité par :

Cyril Cohen Solal
Nom commercial : Upnov IA
Entrepreneur individuel
Adresse : 84 avenue Secrétan, 75019 Paris, France
SIREN : 939 461 935
SIRET : 939 461 935 00016
Email : [contact@upnov.com](mailto:contact@upnov.com)
Téléphone : 01 83 80 16 68

## 2. Directeur de la publication

Le directeur de la publication est :
Cyril Cohen Solal

## 3. Hébergement

Le site est hébergé par :

OVH SAS
2 rue Kellermann
BP 80157
59053 Roubaix Cedex 1
France
Téléphone : 1007

## 4. Propriété intellectuelle

L'ensemble des contenus présents sur le site https://www.upnov.com, notamment les textes, images, graphismes, logos, vidéos, icônes, sons, éléments visuels, structure et architecture du site, sont protégés par le droit de la propriété intellectuelle.

Sauf autorisation écrite préalable, toute reproduction, représentation, modification, publication, adaptation ou exploitation, totale ou partielle, des éléments du site est interdite.

Toute utilisation non autorisée du site ou de l'un quelconque de ses contenus pourra être considérée comme constitutive d'une contrefaçon et poursuivie conformément aux dispositions applicables du Code de la propriété intellectuelle.

## 5. Responsabilité

Upnov IA s'efforce de fournir sur le site https://www.upnov.com des informations aussi précises et à jour que possible.

Toutefois, Upnov IA ne peut garantir l'exactitude, la complétude ou l'actualité permanente des informations diffusées sur le site.

Les informations présentes sur le site sont fournies à titre informatif et ne constituent pas un conseil personnalisé. L'utilisateur reste seul responsable de l'utilisation qu'il fait des informations accessibles sur le site.

Upnov IA ne pourra être tenue responsable des dommages directs ou indirects résultant de l'accès au site, de son utilisation ou de l'impossibilité d'y accéder.

## 6. Données personnelles

Le site peut collecter des données personnelles via ses formulaires de contact, de diagnostic gratuit, de demande d'information, de demande de programme ou de demande de devis.

Pour toute question relative au traitement des données personnelles ou pour exercer vos droits, vous pouvez écrire à :

[dpo@upnov.com](mailto:dpo@upnov.com)

Pour plus d'informations sur la collecte et le traitement des données personnelles, vous pouvez consulter la [Politique de confidentialité](/politique-de-confidentialite/) du site.

## 7. Cookies

Le site peut utiliser des cookies et autres traceurs, notamment pour assurer son bon fonctionnement, mesurer son audience et améliorer l'expérience utilisateur.

Les cookies non nécessaires au fonctionnement du site sont soumis au consentement préalable de l'utilisateur via l'outil de gestion du consentement tarteaucitron.js.

L'utilisateur peut à tout moment modifier ses préférences de cookies via le module prévu à cet effet sur le site.

Pour plus d'informations, vous pouvez consulter la [Politique de confidentialité](/politique-de-confidentialite/).

## 8. Liens hypertextes

Le site https://www.upnov.com peut contenir des liens vers d'autres sites internet.

Upnov IA ne peut être tenue responsable du contenu de ces sites externes ni de leur politique en matière de données personnelles, de cookies ou de sécurité.

## 9. Conditions commerciales

Les prestations proposées par Upnov IA font l'objet d'un devis personnalisé.

Les conditions commerciales applicables sont précisées dans le devis ou les documents contractuels transmis au client avant toute validation de commande.

## 10. Droit applicable

Les présentes mentions légales sont soumises au droit français.

En cas de litige, et sous réserve des dispositions légales impératives applicables, les tribunaux compétents seront ceux désignés conformément aux règles de procédure applicables.
`;

const MentionsLegalesPage = () => {
  return (
    <>
      <Helmet>
        <title>Mentions légales — Upnov IA</title>
        <meta
          name="description"
          content="Mentions légales d'Upnov IA : éditeur du site, hébergeur, propriété intellectuelle et informations légales obligatoires."
        />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Mentions légales — Upnov IA" />
        <meta property="og:description" content="Mentions légales d'Upnov IA : éditeur du site, hébergeur, propriété intellectuelle et informations légales obligatoires." />
        <meta property="og:url" content="https://www.upnov.com/mentions-legales" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/IA-et-conformite.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      <section className="bg-primary text-white py-12 md:py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h1 className="heading-lg mb-4">Mentions légales</h1>
            <p className="text-indigo-200 text-sm">
              <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
              {' '}&rsaquo;{' '}Mentions légales
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: () => null,
                  h2: ({ ...props }) => (
                    <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" {...props} />
                  ),
                  h3: ({ ...props }) => (
                    <h3 className="text-xl font-bold text-gray-800 mt-8 mb-3" {...props} />
                  ),
                  p: ({ ...props }) => (
                    <p className="text-gray-700 mb-4 leading-relaxed" {...props} />
                  ),
                  a: ({ href, children, ...props }) => {
                    if (href && href.startsWith('/')) {
                      return (
                        <Link
                          to={href}
                          className="text-primary hover:underline transition-colors"
                        >
                          {children}
                        </Link>
                      );
                    }
                    return (
                      <a
                        href={href}
                        className="text-primary hover:underline transition-colors"
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  },
                  ul: ({ ...props }) => (
                    <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700" {...props} />
                  ),
                  ol: ({ ...props }) => (
                    <ol className="list-decimal pl-6 space-y-2 mb-6 text-gray-700" {...props} />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MentionsLegalesPage;
